// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArchetypeSpecificStepRunSummaryPanelProps, StepRunSummaryCard } from '.';
import { RolloutStepRunData } from '@/types';
import {
    getAutomationIdByType,
    getIsAppleAppStore,
    isPhasedReleasePaused,
    tryDeterminePhasedReleaseDaysToPercentageSchedule,
} from '@/helpers';
import { AutomationAutoStagedRollout, AutomationType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById, selectStabilityIntegrations } from '@/selectors/selectors';
import {
    PhasedReleaseProgressSection,
    AdoptionRateSection,
} from '@/components/workflowOverview/widgets/WorkflowRunPhasedReleaseWidget';

export const RolloutStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({ stepRun, appId }) => {
    const rolloutData: RolloutStepRunData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const phasedRelease = rolloutData.phasedRelease;

    const automationRuns = stepRun.automationRuns;
    const autoStagedRolloutAutomationId = getAutomationIdByType(automationRuns, AutomationType.AutoStagedRollout);

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;
    const hasPhasedReleaseState = !!phasedRelease;
    const isAppleAppStore = getIsAppleAppStore(rolloutData.appStoreType);
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const daysToPercent = tryDeterminePhasedReleaseDaysToPercentageSchedule(
        hasPhasedReleaseState,
        rolloutData.appStoreType,
        autoStagedRolloutAutomation,
    );

    const wasAccelerated =
        phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout;
    const wasHalted = isPhasedReleasePaused(phasedRelease?.state);

    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));

    if (!phasedRelease) {
        return null;
    }

    const releaseAdoptionsByInstallationId = rolloutData.releaseAdoptions ?? {};
    const firstDestination = stepRun.destinations[0];

    const adoptionEntries = stabilityIntegrations.map((integration) => {
        const adoptions = releaseAdoptionsByInstallationId[integration.installationId] ?? [];
        const latest = adoptions.length > 0 ? adoptions[0] : null;
        const adoptionRate = latest?.adoptionRate ?? 0;
        const destinationAdoptions: [(typeof stepRun.destinations)[0], number][] =
            adoptionRate > 0 && firstDestination ? [[firstDestination, adoptionRate]] : [];
        return {
            installationId: integration.installationId,
            integrationId: integration.id,
            destinationAdoptions,
        };
    });

    const fallbackAdoptionEntries =
        adoptionEntries.length === 0 ?
            [
                {
                    installationId: '__none__',
                    integrationId: undefined,
                    destinationAdoptions: [] as [(typeof stepRun.destinations)[0], number][],
                },
            ]
        :   adoptionEntries;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {fallbackAdoptionEntries.map((entry) => (
                    <StepRunSummaryCard key={entry.installationId}>
                        <AdoptionRateSection
                            destinationAdoptions={entry.destinationAdoptions}
                            stabIntegrationId={entry.integrationId}
                            showExpandButton={false}
                            showTitle={true}
                        />
                    </StepRunSummaryCard>
                ))}
            </div>

            <StepRunSummaryCard>
                <PhasedReleaseProgressSection
                    destinations={stepRun.destinations}
                    percentage={phasedRelease.phasedReleasePercent}
                    dayNumber={phasedRelease.dayNumber}
                    daysToPercent={daysToPercent}
                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                    isAccelerated={wasAccelerated}
                    isRunwayAcceleratedRollout={phasedRelease.isRunwayAcceleratedRollout}
                    isHalted={wasHalted}
                    isRunwayHalted={rolloutData.arePhasedReleaseAutomationsPaused}
                />
            </StepRunSummaryCard>
        </div>
    );
};
